import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Chip,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Autocomplete,
  createFilterOptions
} from '@mui/material';
import { 
  Image as ImageIcon,
  LocalOffer as TagIcon,
  Add as AddIcon
} from '@mui/icons-material';
import TagChip from '../../atoms/TagChip';
import api from '../../../services/api';
import PlatformDialog from '../PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../PlatformDialog/PlatformDialogTitle';

const filter = createFilterOptions();

const TagFormDialog = ({ open, tag, onClose, onSave, onSubmit, title }) => {
  const theme = useTheme();
  // Aceitar onSubmit como alias para onSave
  const handleSave = onSave || onSubmit;
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    classificationType: 'neutral',
    competitorName: '',
  });

  const [errors, setErrors] = useState({
    name: '',
  });

  const [articles, setArticles] = useState([]);
  const [coverArticleId, setCoverArticleId] = useState(tag?.coverArticleId || '');
  const [coverImage, setCoverImage] = useState('');

  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name || '',
        description: tag.description || '',
        classificationType: tag.classificationType || 'neutral',
        competitorName: tag.competitorName || '',
      });
      setErrors({
        name: '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        classificationType: 'neutral',
        competitorName: '',
      });
      setErrors({
        name: '',
      });
    }
  }, [tag]);

  useEffect(() => {
    if (tag && tag._id) {
      // Buscar artigos da tag para seleção de capa
      api.get(`/tags/${tag._id}/articles?limit=100`).then(res => {
        setArticles(res.data.data || []);
      }).catch(err => {
        console.error('Erro ao buscar artigos da tag:', err);
      });
      setCoverArticleId(tag.coverArticleId || '');
      // Se a tag já tem uma imagem de capa, use-a como preview inicial
      if (tag.coverImage) {
        setCoverImage(tag.coverImage);
      }
    } else {
      setArticles([]);
      setCoverArticleId('');
      setCoverImage('');
    }
  }, [tag]);

  useEffect(() => {
    if (coverArticleId && articles.length > 0) {
      // Se um artigo foi escolhido manualmente, use sua imagem
      const found = articles.find(a => a._id === coverArticleId);
      setCoverImage(found?.image || '');
    } else if (articles.length > 0) {
      // Se nenhum artigo foi escolhido, use a imagem do artigo mais recente
      const lastArticle = articles.find(a => a.image);
      setCoverImage(lastArticle?.image || '');
    } else {
      setCoverImage('');
    }
  }, [coverArticleId, articles]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Limpar erro do campo quando ele for editado
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = (e, submittedName) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    const nextFormData = submittedName === undefined
      ? formData
      : { ...formData, name: submittedName };

    if (!nextFormData.name.trim()) {
      setErrors(prev => ({ ...prev, name: 'O nome da tag é obrigatório' }));
      return;
    }
    if (nextFormData.classificationType === 'competitor' && !nextFormData.competitorName.trim()) {
      setErrors(prev => ({ ...prev, competitorName: 'Informe o nome do concorrente' }));
      return;
    }

    const tagName = nextFormData.name.trim();

    const payload = {
      name: tagName,
      description: nextFormData.description.trim(),
      classificationType: nextFormData.classificationType,
      competitorName: nextFormData.classificationType === 'competitor' ? nextFormData.competitorName.trim() : null
    };

    if (tag && tag._id) {
      payload.coverArticleId = coverArticleId || null;
    }
    
    handleSave(payload);
  };

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <PlatformDialogTitle
          title={title || (tag ? 'Editar Tag' : 'Nova Tag')}
          subtitle={tag ? `Editando detalhes da tag ${tag.name}` : 'Crie uma nova tag para organizar seus artigos'}
          icon={<TagIcon />}
          onClose={onClose}
        />
        <DialogContent sx={{ pt: 2, pb: 2, px: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              id="tag-name-autocomplete"
              freeSolo
              inputValue={formData.name}
              options={[]}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setFormData(prev => ({ ...prev, name: newInputValue }));
                }
              }}
              filterOptions={(options, params) => {
                const filtered = [];
                if (params.inputValue !== '') {
                  filtered.push({
                    inputValue: params.inputValue,
                    name: `Criar "${params.inputValue}"`,
                    isNew: true
                  });
                }
                return filtered;
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                if (option.inputValue) return option.inputValue;
                return option.name;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Nome da Tag*"
                  placeholder="Digite o nome da nova tag..."
                  error={!!errors.name}
                  helperText={errors.name || "Pressione Enter ou clique em Criar para salvar"}
                  required
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key || 'new-tag-option'} {...otherProps} onClick={(event) => handleSubmit(event, option.inputValue)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddIcon fontSize="small" color="primary" />
                      <Typography color="primary" fontWeight={600}>{option.name}</Typography>
                    </Box>
                  </li>
                );
              }}
              onChange={(event, newValue) => {
                if (newValue && typeof newValue !== 'string' && newValue.isNew) {
                  setFormData(prev => ({ ...prev, name: newValue.inputValue }));
                  handleSubmit(event, newValue.inputValue);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const val = e.target.value?.trim();
                  if (val) {
                    e.preventDefault();
                    setFormData(prev => ({ ...prev, name: val }));
                    handleSubmit(e, val);
                  }
                }
              }}
            />
          </Box>

          <TextField
            margin="normal"
            name="description"
            label="Descrição (Opcional)"
            placeholder="Breve descrição sobre a tag..."
            type="text"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={handleChange}
            size="small"
            sx={{ 
              mb: 1, 
              '& .MuiOutlinedInput-root': { borderRadius: 2 } 
            }}
          />

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Classificação</InputLabel>
            <Select
              name="classificationType"
              label="Classificação"
              value={formData.classificationType}
              onChange={handleChange}
            >
              <MenuItem value="brand">Marca</MenuItem>
              <MenuItem value="competitor">Concorrente</MenuItem>
              <MenuItem value="neutral">Neutra (editorial/organizacional)</MenuItem>
            </Select>
          </FormControl>

          {formData.classificationType === 'competitor' && (
            <TextField
              margin="normal"
              name="competitorName"
              label="Nome do concorrente*"
              fullWidth
              value={formData.competitorName}
              onChange={handleChange}
              error={!!errors.competitorName}
              helperText={errors.competitorName}
              size="small"
            />
          )}

          {/* Seleção de artigo de capa (apenas para edição) */}
          {tag && tag._id && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" color="text.primary" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon fontSize="small" color="primary" /> Foto de Capa
              </Typography>
              
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Selecionar Artigo para Capa</InputLabel>
                <Select
                  label="Selecionar Artigo para Capa"
                  value={coverArticleId}
                  onChange={e => setCoverArticleId(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>(Usar artigo mais recente automaticamente)</em>
                  </MenuItem>
                  {articles.map(article => (
                    <MenuItem key={article._id} value={article._id}>
                      {article.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mt: 1, mb: 1 }}>
                {coverImage ? (
                  <img 
                    src={coverImage} 
                    alt="Capa escolhida" 
                    style={{ 
                      maxWidth: 180, 
                      maxHeight: 120, 
                      borderRadius: 8,
                      objectFit: 'cover'
                    }} 
                  />
                ) : (
                  <Box 
                    sx={{ 
                      width: 180, 
                      height: 120, 
                      borderRadius: 8,
                      backgroundColor: '#f0f0f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 1
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Sem imagem disponível
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 100,
              fontWeight: 600
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!formData.name.trim()}
            sx={{
              minWidth: 120,
              fontWeight: 700,
              borderRadius: 2,
              boxShadow: 'none'
            }}
          >
            Salvar Tag
          </Button>
        </DialogActions>
      </form>
    </PlatformDialog>
  );
};

TagFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  tag: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    classificationType: PropTypes.oneOf(['brand', 'competitor', 'neutral']),
    competitorName: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default TagFormDialog;
